import { Select, Table, TableColumnsType } from 'antd';
import moment from 'moment';
import QueryString from 'qs';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { getNotices } from '../../../api/homepageManagementHomeNotice.api';
import Button from '../../../components/Button';
import ErrorResponse from '../../../components/ErrorResponse';
import SimpleHeader from '../../../components/layout/SimpleHeader';
import Loader from '../../../components/Loader';
import Pagination from '../../../components/Pagination';
import SelectCampus from '../../../components/SelectCampus';
import useAuth from '../../../hooks/useAuth';
import useAxios from '../../../hooks/useAxios';
import config from '../../../lib/config';
import {
  IGetNotices,
  INoticeItem,
} from '../../../models/homepageManagementHomeNotice.model';
import { setCurrentCampus } from '../../../store/auth.slice';
import { RootState } from '../../../store/rootReducer';
import useUtil from '../../../hooks/useUtil';

const HomepageNotice = () => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { isCampusDeactived } = useUtil();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });

  const { currentCampus } = useSelector((state: RootState) => state.auth);
  const { campusData } = useSelector((state: RootState) => state.campusData);
  const { data: res, isLoading, error, execute } = useAxios<IGetNotices>();

  const [field, setField] = useState<string>('title');
  const [keyword, setKeyword] = useState<string>('');
  const [page, setPage] = useState(1);
  const [param, setParam] = useState({});
  const [campusDeactived, setCampusDeactived] = useState(false);

  useEffect(() => {
    setCampusDeactived(isCampusDeactived());
  }, [isCampusDeactived]);

  useEffect(() => {
    if (currentCampus) {
      const _param: any = {
        pageNo: page,
        perPage: config.pageSize,
      };

      if (query.campusCode) {
        dispatch(setCurrentCampus(String(query.campusCode)));
        _param.campusCode = String(query.campusCode);
      }
      if (query.page) {
        _param.pageNo = Number(query.page);
        setPage(Number(query.page));
      }
      if (query.title) {
        _param.title = String(query.title);
        setField('title');
        setKeyword(String(query.title));
      }
      if (query.regMemName) {
        _param.regMemName = String(query.regMemName);
        setField('regMemName');
        setKeyword(String(query.regMemName));
      }

      execute(getNotices(currentCampus, _param));
      _param.page = _param.pageNo;
      delete _param.pageNo;
      delete _param.perPage;
      setParam(_param);
    }
  }, [currentCampus, location]);

  const getCampusName = (campusCode: string) => {
    const campus = campusData.find((item) => item.campusCode === campusCode);
    return campus?.campusKorName || campusCode;
  };

  const onSearch = () => {
    const param: any = { page: 1 };
    param.campusCode = currentCampus;
    switch (field) {
      case 'title':
        param.title = keyword;
        break;
      case 'regMemName':
        param.regMemName = keyword;
        break;
    }

    history.push(`/homepage/notice?${QueryString.stringify(param)}`);
  };

  const columns: TableColumnsType<INoticeItem> = [
    {
      title: t('homepage.archive.column1'),
      dataIndex: 'noticeCode',
      key: 'noticeCode',
      align: 'center',
      width: '6%',
      sorter: (a, b) => a.noticeCode - b.noticeCode,
      render: (value, record) => {
        if (record.bulletinSeq === 1) {
          return <span className="text-bold">{t('homepage.notice.text1')}</span>;
        } else {
          return value;
        }
      },
    },
    {
      title: t('homepage.notice.column1'),
      dataIndex: 'openYn',
      key: 'openYn',
      align: 'center',
      width: '10%',
      sorter: (a, b) => a.openYn?.localeCompare(b.openYn),
      render: (value) => (value === 'Y' ? t('common.text.public') : t('common.text.private')),
    },
    {
      title: t('common.menu.campus'),
      dataIndex: 'campusCode',
      key: 'campusCode',
      align: 'center',
      width: '10%',
      sorter: (a, b) => a.campusCode?.localeCompare(b.campusCode),
      render: (value) => getCampusName(value),
    },
    {
      title: t('common.column.title'),
      dataIndex: 'title',
      key: 'title',
      align: 'center',
      sorter: (a, b) => a.title?.localeCompare(b.title),
      ellipsis: true,
      render: value => {
        return <div className="text-left ant-table-cell-ellipsis">{value}</div>
      }
    },
    {
      title: t('homepage.archive.column5'),
      dataIndex: 'regDate',
      key: 'regDate',
      align: 'center',
      width: '10%',
      sorter: (a, b) => a.regDate?.localeCompare(b.regDate),
      render: (value) => moment(value).format('YYYY.MM.DD'),
    },
    {
      title: t('homepage.archive.column6'),
      dataIndex: 'regMemKorName',
      key: 'regMemKorName',
      align: 'center',
      width: '10%',
      sorter: (a, b) => a.regMemKorName?.localeCompare(b.regMemKorName),
    },
    {
      title: t('homepage.archive.column7'),
      dataIndex: 'readCount',
      key: 'readCount',
      align: 'center',
      width: '10%',
      sorter: (a, b) => a.readCount - b.readCount,
    },
  ];

  return (
    <>
    <SimpleHeader
      iconFile="icon-homepage-big.svg"
      parentMenu={t('common.menu.homepage')}
      currentMenu={t('homepage.notice.title1')}
      onlyTitle={true}
    />
    <div className="common-search">
      <div className="search-area">
        <ul className="search-bx">
          <li>
            <label>{t('common.menu.campus')}</label>
            <div>
              <SelectCampus />
            </div>
          </li>
          <li>
            <div>
              <Select
                placeholder={t('common.button.select')}
                value={field}
                onChange={(value) => setField(value)}
              >
                <Select.Option value="title">{t('common.column.title')}</Select.Option>
                <Select.Option value="regMemName">{t('homepage.archive.column6')}</Select.Option>
              </Select>
            </div>
            <input
              type="text"
              className="form-control w-240p"
              placeholder={t('homepage.notice.placeholder1')}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') onSearch();
              }}
            />
            <div className="comm-btn__area">
              <Button
                className="btn-basic small black"
                onClick={onSearch}
                text={t('common.button.search')}
              />
            </div>
          </li>
        </ul>
      </div>
    </div>
      <div className="content scrollbar">
        {isLoading ? <Loader /> : null}
        <ErrorResponse error={error} />

        <div className="col-frame">
          <div className="comm-bx__between">
            <div className="comm-sch_result">{t('common.text.count')}: <b>{res?.total}</b>{t('common.text.unit')}</div>
            <div className="comm-btn__area">
              <Button
                className="btn btn-primary btn-w100p"
                onClick={() => history.push('/homepage/notice/create')}
                text={t('event.education.button1')}
                disabled={campusDeactived}
                writeAuthYn={writeAuthYn}
              />
            </div>
          </div>
          <div className='comm-table__area'>
            <Table
              columns={columns}
              dataSource={res?.data}
              pagination={{
                current: page,
                pageSize: config.pageSize,
              }}
              onRow={(record) => {
                return {
                  onClick: () => {
                    history.push(
                      `/homepage/notice/${record.campusCode}/${record.noticeCode}${location.search}`,
                    );
                  },
                };
              }}
              rowClassName="pointer"
              rowKey="noticeCode"
            />
          </div>
          <div className="comm-bx__between">
            <div />
            <div className="common-paging center">
              <Pagination
                count={res?.total || 0}
                page={page}
                param={param}
                path="/homepage/notice"
              />
            </div>
            <div className="comm-btn__area right">
              <Button
                className="btn-basic pink"
                onClick={() => history.push('/homepage/notice/create')}
                text={t('event.education.button1')}
                disabled={campusDeactived}
                writeAuthYn={writeAuthYn}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HomepageNotice;
